import React, { ButtonHTMLAttributes, forwardRef } from 'react';
import { cn } from 'utils/helpers';
import { useLicenseGate } from './LicenseGate';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Defaults to `button` so actions inside forms do not reload the page. Use `submit` for save actions. */
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger' | 'warning';
  size?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
  isLoading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  /** When true, license expiry does not disable this button (e.g. Back). */
  navigationAction?: boolean;
  /** When true, hide instead of disable when license restricts actions (e.g. Save, Add). */
  hideOnLicenseExpired?: boolean;
  disableOnLicenseExpired?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      className,
      variant = 'primary',
      size = 'md',
      fullWidth = false,
      isLoading = false,
      disabled,
      leftIcon,
      rightIcon,
      navigationAction = false,
      hideOnLicenseExpired = false,
      disableOnLicenseExpired = true,
      type = 'button',
      ...props
    },
    ref
  ) => {
    const { shouldDisable } = useLicenseGate();
    const licenseBlocksActions = shouldDisable && !navigationAction;

    if (hideOnLicenseExpired && shouldDisable) {
      return null;
    }
    const baseStyles =
      'inline-flex items-center justify-center font-medium rounded-xl transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation';

    const variants = {
      primary:
        'bg-primary-600 text-white hover:bg-primary-700 focus-visible:ring-primary-500 shadow-sm',
      secondary:
        'bg-secondary-100 text-secondary-900 hover:bg-secondary-200 focus-visible:ring-secondary-500',
      outline:
        'border border-primary-300 bg-white text-primary-700 hover:bg-primary-50 focus-visible:ring-primary-500',
      ghost: 'text-secondary-700 hover:bg-secondary-100 focus-visible:ring-secondary-500',
      danger:
        'bg-rose-600 text-white hover:bg-rose-700 focus-visible:ring-rose-500 shadow-sm',
      warning:
        'bg-amber-500 text-white hover:bg-amber-600 focus-visible:ring-amber-500 shadow-sm',
    };

    const sizes = {
      sm: 'px-3.5 py-2 sm:py-1.5 text-sm gap-1.5 min-h-[44px] sm:min-h-[34px]',
      md: 'px-4 py-2.5 sm:py-2 text-sm sm:text-base gap-2 min-h-[44px] sm:min-h-[40px]',
      lg: 'px-6 py-3 sm:py-3 text-base sm:text-lg gap-2.5 min-h-[48px] sm:min-h-[46px]',
    };

    return (
      <button
        ref={ref}
        type={type}
        className={cn(
          baseStyles,
          variants[variant],
          sizes[size],
          fullWidth && 'w-full',
          className
        )}
        disabled={disabled || isLoading || (disableOnLicenseExpired && licenseBlocksActions)}
        {...props}
      >
        {isLoading && (
          <svg
            className="animate-spin h-5 w-5"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
        )}
        {!isLoading && leftIcon && <span>{leftIcon}</span>}
        {children}
        {!isLoading && rightIcon && <span>{rightIcon}</span>}
      </button>
    );
  }
);

Button.displayName = 'Button';

